import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isValid, parseISO } from 'date-fns';
import { didCancel, dropTask, restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { and, eq, not } from 'ember-truth-helpers';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import UiCard from 'garaje/components/ui/card';
import type { DocumentStatus } from 'garaje/components/visitors/document-status-badge';
import DocumentStatusBadge from 'garaje/components/visitors/document-status-badge';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type VisitorDocumentModel from 'garaje/models/visitor-document';

interface Signature {
  Args: {
    document?: VisitorDocumentModel;
    template?: CustomUserDocumentTemplateModel;
    isUploaded: boolean;
    showUndo?: boolean;
    onStatusChange?: () => void;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewCard extends Component<Signature> {
  @service declare store: StoreService;
  @tracked primaryExpirationDate: Date | null = null;
  @tracked expirationEntries: Array<{ name: string; date: Date }> = [];
  /** Tracks which calendar popover is open: 'primary', a numeric index, or null */
  @tracked openCalendar: 'primary' | number | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.initExpirationEntries();
  }

  private parseDate(dateStr: string): Date | null {
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  }

  private initExpirationEntries(): void {
    const data = this.args.document?.inputFieldsData ?? {};

    // Initialize pinned "Expiration date" from the reserved key
    const primaryDateStr = data['expiration_date'];
    if (typeof primaryDateStr === 'string') {
      this.primaryExpirationDate = this.parseDate(primaryDateStr);
    }

    // Remaining entries exclude the reserved key
    this.expirationEntries = Object.entries(data)
      .filter(([key, v]) => key !== 'expiration_date' && typeof v === 'string')
      .map(([name, dateStr]) => {
        const parsed = this.parseDate(String(dateStr));
        return { name, date: parsed ?? new Date() };
      });
  }

  get title(): string | undefined {
    return this.args.isUploaded ? this.args.document?.title : this.args.template?.title;
  }

  get status(): DocumentStatus {
    if (!this.args.isUploaded) return 'not-uploaded';
    const doc = this.args.document;
    if (doc?.isApproved) return 'approved';
    if (doc?.isDenied) return 'denied';
    return 'review';
  }

  get uploadDate(): string | null {
    const attachment = this.args.document?.mostRecentAttachment;
    const date = attachment?.updatedAt;
    if (!date) {
      const docDate = this.args.document?.updatedAt;
      return docDate ? format(docDate, 'MMM d, yyyy, h:mm a') : null;
    }
    return format(date, 'MMM d, yyyy, h:mm a');
  }

  get externalAttachmentUrl(): string | null {
    return this.args.document?.externalAttachments?.[0]?.url ?? null;
  }

  get documentLink(): VisitorDocumentModel['userDocumentLink'] {
    return this.args.document?.userDocumentLink;
  }

  get isPending(): boolean {
    return this.status === 'review';
  }

  get approveDisabled(): boolean {
    return this.approveTask.isRunning || !this.primaryExpirationDate;
  }

  get hasPrimaryExpirationDate(): boolean {
    return this.primaryExpirationDate !== null;
  }

  get primaryExpirationDateDisplay(): string {
    return this.primaryExpirationDate ? format(this.primaryExpirationDate, 'MMM d, yyyy') : 'Select date';
  }

  formatDate = (date: Date): string => format(date, 'MMM d, yyyy');

  @action toggleCalendar(id: 'primary' | number): void {
    if (this.openCalendar === id) {
      this.closeCalendar();
    } else {
      this.openCalendar = id;
      // Defer so the current click doesn't immediately close
      requestAnimationFrame(() => {
        if (this.isDestroying || this.isDestroyed) return;
        document.addEventListener('click', this.handleOutsideClick, true);
      });
    }
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (
      target.closest(
        '[data-test-primary-expiration-calendar], [data-test-expiration-calendar], [data-test-primary-expiration-date], [data-test-expiration-date]',
      )
    )
      return;
    this.closeCalendar();
  };

  closeCalendar = (): void => {
    this.openCalendar = null;
    document.removeEventListener('click', this.handleOutsideClick, true);
  };

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick, true);
  }

  @action async selectPrimaryExpirationDate(date: Date): Promise<void> {
    this.primaryExpirationDate = date;
    this.closeCalendar();
    // Save directly — primary date should not be blocked by incomplete custom entries
    await this.patchInputFieldsData();
  }

  @action updatePrimaryExpirationDate(date: Date | null): void {
    if (!date) return;
    this.primaryExpirationDate = date;
    void this.saveExpirationDatesTask.perform();
  }

  get isDecided(): boolean {
    return this.status === 'approved' || this.status === 'denied';
  }

  @action addExpirationRow(): void {
    this.expirationEntries = [...this.expirationEntries, { name: '', date: new Date() }];
  }

  @action removeExpirationRow(index: number): void {
    this.expirationEntries = this.expirationEntries.filter((_, i) => i !== index);
    void this.saveExpirationDatesTask.perform();
  }

  @action updateExpirationName(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, name: value } : e));
    void this.saveExpirationDatesTask.perform();
  }

  @action selectExpirationDate(index: number, date: Date): void {
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, date } : e));
    this.closeCalendar();
    void this.saveExpirationDatesTask.perform();
  }

  private buildInputFieldsData(): Record<string, string> {
    const data: Record<string, string> = {};
    if (this.primaryExpirationDate) {
      data['expiration_date'] = format(this.primaryExpirationDate, 'yyyy-MM-dd');
    }
    for (const entry of this.expirationEntries) {
      if (entry.name && entry.name !== 'expiration_date' && entry.date) {
        data[entry.name] = format(entry.date, 'yyyy-MM-dd');
      }
    }
    return data;
  }

  get hasIncompleteExpirationEntries(): boolean {
    return this.expirationEntries.some((e) => (e.name && !e.date) || (!e.name && e.date));
  }

  // Send a targeted PATCH with input_fields_data. Include `active` to
  // preserve its current value — the response pushes all attributes
  // back into the store.
  private async patchInputFieldsData(): Promise<void> {
    const doc = this.args.document;
    if (!doc) return;
    const data = this.buildInputFieldsData();
    const adapter = this.store.adapterFor('visitor-document');
    const url = adapter.buildURL('visitor-document', doc.id, null, 'v3');
    await adapter.ajax(url, 'PATCH', {
      data: {
        data: {
          type: 'visitor-documents',
          id: doc.id,
          attributes: {
            'input-fields-data': data,
            active: doc.active,
          },
        },
      },
    });
    doc.inputFieldsData = Object.freeze({ ...data });
  }

  // Debounced auto-save: restartable so rapid edits collapse into one PATCH.
  // Skips save if any entry is incomplete (name without date or vice versa).
  saveExpirationDatesTask = restartableTask(async () => {
    try {
      await timeout(500);
      if (this.hasIncompleteExpirationEntries) return;
      await this.patchInputFieldsData();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  approveTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.patchInputFieldsData();
      await this.documentLink?.approve();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  denyTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.documentLink?.deny();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  resetTask = dropTask(async () => {
    try {
      await this.documentLink?.reset();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  <template>
    <UiCard @border={{true}} class="rounded-lg p-6 mb-4 overflow-visible" data-test-document-review-card ...attributes>
      {{! Header: title + status badge }}
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold m-0" data-test-document-title>{{this.title}}</h3>
        <DocumentStatusBadge @status={{this.status}} />
      </div>

      {{#if @isUploaded}}
        {{! Upload date }}
        {{#if this.uploadDate}}
          <p class="text-sm text-carbon-50 mt-0 mb-4" data-test-upload-date>
            Uploaded
            {{this.uploadDate}}
          </p>
        {{/if}}

        {{! Document preview image }}
        {{#if this.externalAttachmentUrl}}
          <div
            class="relative rounded-md border border-carbon-20 overflow-hidden mb-4 bg-carbon-10"
            data-test-document-preview
          >
            <img
              src={{this.externalAttachmentUrl}}
              alt={{this.title}}
              class="w-full object-contain max-h-80"
              data-private
            />
            <div class="flex justify-center py-2 bg-white border-t border-carbon-20">
              <a
                href={{this.externalAttachmentUrl}}
                download
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn--small btn--outline"
                data-test-download-button
              >
                ↓ Download
              </a>
            </div>
          </div>
        {{/if}}

        {{! Expiration date fields }}
        <div class="mt-4" data-test-expiration-fields>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-carbon-70">Expiration date(s)</span>
            <button
              class="text-sm text-gem-50 hover:underline"
              type="button"
              data-test-add-expiration
              {{on "click" this.addExpirationRow}}
            >
              Add
            </button>
          </div>

          {{! Pinned primary expiration date — always visible, not removable }}
          <div class="flex items-start gap-2 mb-2" data-test-primary-expiration-row>
            <input
              type="text"
              value="Expiration date"
              disabled
              class="flex-1 text-sm border rounded px-2 py-1 h-8 border-carbon-20 bg-carbon-10 text-carbon-60 cursor-default"
              data-test-primary-expiration-label
            />
            <div class="relative">
              <button
                type="button"
                class="flex items-center justify-center w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                  {{if (eq this.openCalendar 'primary') 'border-gem-50' 'border-carbon-20'}}"
                data-test-primary-expiration-date
                {{on "click" (fn this.toggleCalendar "primary")}}
              >
                <span class="{{unless this.hasPrimaryExpirationDate 'text-carbon-40'}}">
                  {{this.primaryExpirationDateDisplay}}
                </span>
              </button>
              {{! template-lint-disable use-ui-card }}
              {{#if (eq this.openCalendar "primary")}}
                <div
                  class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                  data-test-primary-expiration-calendar
                >
                  <EnvoyOneUpCalendar
                    @selectedDate={{this.primaryExpirationDate}}
                    @onSelectDate={{this.selectPrimaryExpirationDate}}
                  />
                </div>
              {{/if}}
            </div>
            {{! Spacer to align with removable rows }}
            <div class="flex-none w-8 h-8"></div>
          </div>

          {{! Additional custom expiration entries }}
          {{#each this.expirationEntries as |entry index|}}
            <div class="flex items-start gap-2 mb-2" data-test-expiration-row>
              <input
                type="text"
                value={{entry.name}}
                placeholder="Name"
                class="flex-1 text-sm border rounded px-2 py-1 h-8
                  {{if (and (not entry.name) entry.date) 'border-red-50' 'border-carbon-20'}}"
                required
                data-test-expiration-name
                {{on "change" (fn this.updateExpirationName index)}}
              />
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center justify-center w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                    {{if (eq this.openCalendar index) 'border-gem-50' 'border-carbon-20'}}"
                  data-test-expiration-date
                  {{on "click" (fn this.toggleCalendar index)}}
                >
                  <span>{{this.formatDate entry.date}}</span>
                </button>
                {{! template-lint-disable use-ui-card }}
                {{#if (eq this.openCalendar index)}}
                  <div
                    class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                    data-test-expiration-calendar
                  >
                    <EnvoyOneUpCalendar
                      @selectedDate={{entry.date}}
                      @onSelectDate={{fn this.selectExpirationDate index}}
                    />
                  </div>
                {{/if}}
              </div>
              <button
                class="flex-none w-8 h-8 flex items-center justify-center text-carbon-50 hover:text-red-50"
                type="button"
                data-test-remove-expiration
                {{on "click" (fn this.removeExpirationRow index)}}
              >
                ✕
              </button>
            </div>
          {{/each}}
          {{#if this.hasIncompleteExpirationEntries}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-validation>
              Both name and date are required for each entry.
            </p>
          {{/if}}
        </div>

        {{! Approve / Deny / Undo buttons }}
        {{#if this.isPending}}
          {{#unless this.hasPrimaryExpirationDate}}
            <p class="text-xs text-red-50 mt-2 mb-0" data-test-expiration-required>
              Expiration date is required before approving
            </p>
          {{/unless}}
          <div class="flex gap-4 mt-4" data-test-document-actions>
            <button
              class="btn btn--outline flex-1 text-cilantro-60 border-cilantro-60 hover:bg-cilantro-10"
              data-test-approve-button
              disabled={{this.approveDisabled}}
              type="button"
              {{on "click" (perform this.approveTask)}}
            >
              ✓ Approve
            </button>
            <button
              class="btn btn--outline flex-1 text-red-60 border-red-60 hover:bg-red-10"
              data-test-deny-button
              disabled={{this.denyTask.isRunning}}
              type="button"
              {{on "click" (perform this.denyTask)}}
            >
              ✕ Deny
            </button>
          </div>
        {{else if (and this.isDecided @showUndo)}}
          <div class="flex mt-4" data-test-document-actions>
            <button
              class="btn btn--small btn--outline"
              data-test-undo-button
              disabled={{this.resetTask.isRunning}}
              type="button"
              {{on "click" (perform this.resetTask)}}
            >
              Undo
            </button>
          </div>
        {{/if}}
      {{else}}
        {{! Not uploaded placeholder }}
        <div class="py-8 text-center text-carbon-50" data-test-not-uploaded-placeholder>
          Document not yet uploaded
        </div>
      {{/if}}
    </UiCard>
  </template>
}
